/* "/" - the landing page: first, "Where do you want to go?" on a big turning globe (a tap zooms in on the closest
   university and "Let's begin" opens the planner with it: landing/GlobeHero.tsx); then what PathsAbroad is, shown with
   small examples from the app: how it works, step by step, with the app playing each step (landing/How.tsx); what it
   does; how it protects students; that it's free (and how PathsAbroad is paid); and common questions. Every claim here is something the
   product does today. The university and country counts come from the school seed (a unit test keeps them in step).
   It's for people who aren't signed in: the router sends a signed-in student to their own home page. */
import { type ReactNode } from 'react';
import { PLANS_FOR_SALE, WEB_ROUTES, type Plan } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link } from '../app/nav';
import { useDocumentTitle } from '../app/hooks';
import { Icon } from '../app/icons';
import { formatMoney, localeOf } from '../lib/format';
import { type MessageKey } from '../lib/i18n';
import {
  DemoApply, DemoChat, DemoChecklist, DemoEncryption, DemoInbox, DemoLanguages, DemoMatches, DemoParent, DemoReminder,
} from './landing/Demos';
import { GlobeHero } from './landing/GlobeHero';
import { HowItWorks } from './landing/How';
import { Money } from './landing/Money';
import { COUNTRIES, UNIVERSITIES } from './landing/facts';

export function Home() {
  useDocumentTitle('');
  return (
    <div className="land">
      <GlobeHero />
      <Facts />
      <Money />
      <HowItWorks />
      <Features />
      <Trust />
      <Pricing />
      <Faq />
      <Final />
    </div>
  );
}

/* four facts, on one instrument strip */
function Facts() {
  const { t, lang } = useT();
  const n = (v: number) => new Intl.NumberFormat(localeOf(lang)).format(v);
  const facts: Array<[string, () => ReactNode, string]> = [
    ['tile--teal', Icon.map, t('land.facts.unis', { unis: n(UNIVERSITIES), countries: n(COUNTRIES) })],
    ['tile--blue', Icon.globe, t('land.facts.langs')],
    ['tile--indigo', Icon.puzzle, t('land.facts.portals')],
    ['tile--green', Icon.shield, t('land.facts.ads')],
  ];
  return (
    <section className="land-facts" aria-label={t('land.facts.label')}>
      <div className="land-wrap">
        <ul className="land-facts__list">
          {facts.map(([tone, FactIcon, text]) => (
            <li key={tone}><span className={`tile ${tone}`} aria-hidden="true"><FactIcon /></span><span>{text}</span></li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SectionHead({ id, title, lead }: { id: string; title: string; lead?: string }) {
  return (
    <header className="land-head">
      <h2 id={id} className="land-h2">{title}</h2>
      {lead && <p className="land-sub">{lead}</p>}
    </header>
  );
}

function Points({ keys }: { keys: MessageKey[] }) {
  const { t } = useT();
  return <ul className="land-points">{keys.map(k => <li key={k}><Icon.check />{t(k)}</li>)}</ul>;
}

function Feature({ id, eyebrow, title, text, points, demo, note, flip }: {
  id: string; eyebrow: MessageKey; title: MessageKey; text: MessageKey; points: MessageKey[]; demo: ReactNode; note?: MessageKey; flip?: boolean;
}) {
  const { t } = useT();
  return (
    <article className={flip ? 'land-feature land-feature--flip' : 'land-feature'} aria-labelledby={id}>
      <div className="land-feature__copy">
        <p className="land-eyebrow">{t(eyebrow)}</p>
        <h3 id={id} className="land-h3 land-h3--big">{t(title)}</h3>
        <p className="land-feature__text">{t(text)}</p>
        <Points keys={points} />
        {note && <p className="land-feature__note">{t(note)}</p>}
      </div>
      <div className="land-feature__demo">{demo}</div>
    </article>
  );
}

function Tile({ id, tone, icon, title, text, demo, wide }: {
  id: string; tone: string; icon: ReactNode; title: MessageKey; text: MessageKey; demo: ReactNode; wide?: boolean;
}) {
  const { t } = useT();
  return (
    <article className={wide ? 'land-tile land-tile--wide' : 'land-tile'} aria-labelledby={id}>
      <div className="land-tile__copy">
        <span className={`tile ${tone}`} aria-hidden="true">{icon}</span>
        <h3 id={id} className="land-h3">{t(title)}</h3>
        <p className="land-tile__text">{t(text)}</p>
      </div>
      <div className="land-tile__demo">{demo}</div>
    </article>
  );
}

function Features() {
  const { t } = useT();
  return (
    <section id="features" className="land-section land-section--white" aria-labelledby="features-title">
      <div className="land-wrap">
        <SectionHead id="features-title" title={t('land.features.title')} lead={t('land.features.lead')} />
        <div className="land-features">
          <Feature id="f-match" eyebrow="land.match.eyebrow" title="land.match.title" text="land.match.text"
            points={['land.match.p1', 'land.match.p2', 'land.match.p3']} demo={<DemoMatches />} />
          <Feature id="f-apply" eyebrow="land.apply.eyebrow" title="land.apply.title" text="land.apply.text"
            points={['ext.rule3', 'ext.rule4', 'land.apply.p3']} note="ext.notAffiliated" demo={<DemoApply />} flip />
          <Feature id="f-inbox" eyebrow="land.inbox.eyebrow" title="land.inbox.title" text="land.inbox.text"
            points={['land.inbox.p1', 'land.inbox.p2', 'land.inbox.p3']} demo={<DemoInbox />} />
        </div>
        <div className="land-tiles">
          <Tile id="f-ai" tone="tile--indigo" icon={<Icon.info />} title="land.ai.title" text="land.ai.text" demo={<DemoChat />} wide />
          <Tile id="f-rem" tone="tile--red" icon={<Icon.bell />} title="land.reminders.title" text="land.reminders.text" demo={<DemoReminder />} />
          <Tile id="f-visa" tone="tile--orange" icon={<Icon.map />} title="land.visa.title" text="land.visa.text" demo={<DemoChecklist />} />
          <Tile id="f-family" tone="tile--teal" icon={<Icon.people />} title="land.family.title" text="land.family.text" demo={<DemoParent />} />
          <Tile id="f-lang" tone="tile--blue" icon={<Icon.globe />} title="land.lang.title" text="land.lang.text" demo={<DemoLanguages />} />
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const { t } = useT();
  const items: Array<[string, () => ReactNode, MessageKey, MessageKey]> = [
    ['t-rls', Icon.person, 'land.trust.rls.title', 'land.trust.rls.text'],
    ['t-redact', Icon.eyeOff, 'land.trust.redact.title', 'land.trust.redact.text'],
    ['t-ai', Icon.ok, 'land.trust.ai.title', 'land.trust.ai.text'],
    ['t-mfa', Icon.shield, 'sec.mfaTitle', 'land.trust.mfa.text'],
    ['t-help', Icon.bell, 'land.trust.help.title', 'land.trust.help.text'],
    ['t-data', Icon.archive, 'land.trust.data.title', 'land.trust.data.text'],
  ];
  return (
    <section id="trust" className="land-section land-trust night" aria-labelledby="trust-title">
      <div className="land-wrap land-trust__grid">
        <div className="land-trust__intro">
          <h2 id="trust-title" className="land-h2">{t('land.trust.title')}</h2>
          <p className="land-sub">{t('land.trust.lead')}</p>
          <div className="land-trust__enc">
            <h3 className="land-h3">{t('land.trust.enc.title')}</h3>
            <p>{t('land.trust.enc.text')}</p>
            <DemoEncryption />
          </div>
        </div>
        <ul className="land-trust__list">
          {items.map(([id, ItemIcon, title, text]) => (
            <li key={id}>
              <span className="land-trust__icon" aria-hidden="true"><ItemIcon /></span>
              <div>
                <h3 id={id} className="land-h3">{t(title)}</h3>
                <p>{t(text)}</p>
                {id === 't-ai' && <p className="land-trust__link"><Link to={WEB_ROUTES.aiCheck}>{t('land.trust.aiCheck')}</Link></p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  const { t, lang } = useT();
  return (
    <article className="land-plan land-plan--best" aria-labelledby={`plan-${plan.id}`}>
      <div className="land-plan__head">
        <h3 id={`plan-${plan.id}`} className="land-plan__name">{plan.name[lang]}</h3>
      </div>
      <p className="land-plan__price" dir="ltr">{formatMoney(0, lang)}</p>
      <p className="land-plan__period">{t('land.price.freeNote')}</p>
      <ul className="land-plan__list">
        {plan.includes[lang].map((item, i) => <li key={i}><Icon.check /><span>{item}</span></li>)}
      </ul>
    </article>
  );
}

/* It's free (since 3 November 2026, packages/core EVERYTHING_FREE): one card with everything, how PathsAbroad is
   paid (universities' and student services' commissions, never students: said plainly, as the agent rules ask) and
   that it's for studying outside your own country. */
function Pricing() {
  const { t } = useT();
  return (
    <section id="pricing" className="land-section" aria-labelledby="pricing-title">
      <div className="land-wrap">
        <SectionHead id="pricing-title" title={t('land.price.title')} lead={t('land.price.lead')} />
        <div className="land-plans land-plans--one">
          {PLANS_FOR_SALE.map(p => <PlanCard key={p.id} plan={p} />)}
        </div>
        <div className="land-price__foot">
          <p className="land-price__refund"><Icon.info />{t('land.price.how')}</p>
          <p className="land-price__fine">{t('land.price.abroad')}</p>
          <a href={WEB_ROUTES.planner} className="btn btn--primary land-btn">{t('land.cta.free')}</a>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const { t, lang } = useT();
  const n = (v: number) => new Intl.NumberFormat(localeOf(lang)).format(v);
  const qa: Array<[MessageKey, MessageKey]> = [
    ['land.faq.q1', 'land.faq.a1'], ['land.faq.q2', 'land.faq.a2'], ['land.faq.q3', 'land.faq.a3'], ['land.faq.q4', 'land.faq.a4'],
    ['land.faq.q5', 'land.faq.a5'], ['land.faq.q6', 'land.faq.a6'], ['land.faq.q7', 'land.faq.a7'], ['land.faq.q8', 'land.faq.a8'],
  ];
  return (
    <section id="faq" className="land-section land-section--white" aria-labelledby="faq-title">
      <div className="land-wrap land-faq">
        <SectionHead id="faq-title" title={t('land.faq.title')} />
        <div className="land-faq__list">
          {qa.map(([q, a]) => (
            <details key={q} className="land-faq__item">
              <summary><span>{t(q)}</span><Icon.down /></summary>
              <p>{t(a, { unis: n(UNIVERSITIES), countries: n(COUNTRIES) })}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Final() {
  const { t } = useT();
  return (
    <section className="land-final" aria-labelledby="final-title">
      <div className="land-wrap">
        <div className="land-final__card night">
          <h2 id="final-title" className="land-h2">{t('land.final.title')}</h2>
          <p className="land-final__text">{t('land.final.text')}</p>
          <div className="land-cta land-cta--center">
            <a href={WEB_ROUTES.planner} className="btn land-btn land-btn--white">{t('land.cta.free')}</a>
          </div>
        </div>
      </div>
    </section>
  );
}
